import type { SchedulerConstraint } from '../hooks/useSchedulerConstraints'
import type { SchedulerViolation } from '../hooks/useSchedulerGeneration'
import {
  getConstraintDisplayName,
  formatConstraintParameterSummary,
  mapCategoryToDisplay,
} from '../constants'
import {
  classifyViolation,
  countViolations,
  dedupeViolations,
  humanizeWeekRefs,
  resolveTeamLabel,
  type ViolationSeverity,
} from './violations'

/**
 * Pure, DOM-free view-model for the "Constraints & Violations" panel.
 *
 * It answers "here are the rules you defined, and here's exactly what tripped
 * each one." Groups by constraint code (a readable heading) while preserving
 * rule identity by `constraint.id` — a single code can carry multiple enabled
 * rows (e.g. `pair_matchup_count` → "Games per Team", per-team `preferred_time`),
 * so two rows that share a code are NEVER collapsed.
 *
 * Scope-matching a violation back to the specific rule that defined it needs
 * more than the constraint + violation alone: the solver tags violations with
 * affected teams/games, not the originating `constraint.id`. So team→division
 * and game→division lookups are passed in explicitly. Nothing is ever dropped:
 * a violation that can't be attached to exactly one enabled rule lands in the
 * group's `orphanViolations` (or its own orphan group when the code has no
 * defined rule at all).
 */

export type ConstraintReportStatus =
  | 'conflict'
  | 'setup_impossible'
  | 'advisory'
  | 'limited_by_setup'
  | 'ok'
  | 'disabled'

/**
 * A stronger hard rule that shrank the feasible envelope below the target, as
 * carried in `context.blocked_by`. Surfaced under a `limited_by_setup` /
 * `setup_impossible` entry so the panel can say WHY a target was unreachable.
 */
export interface BlockedByEntry {
  constraintType: string
  detail: string | null
}

const readBlockedBy = (ctx: Record<string, unknown>): BlockedByEntry[] => {
  const raw = ctx.blocked_by
  if (!Array.isArray(raw)) {
    return []
  }
  const out: BlockedByEntry[] = []
  for (const b of raw) {
    if (b && typeof b === 'object') {
      const code = (b as Record<string, unknown>).constraint_type
      if (typeof code === 'string' && code.length > 0) {
        const detail = (b as Record<string, unknown>).detail
        out.push({
          constraintType: code,
          detail: typeof detail === 'string' ? detail : null,
        })
      }
    }
  }
  return out
}

export interface ViolationEntry {
  severity: ViolationSeverity
  /**
   * The constraint-outcome classification from `context.outcome`, when present
   * (`forced_shortfall`, `avoidable_violation`, `unknown`, …). Null for
   * historical rows / deploy-skew payloads that predate outcome semantics — those
   * fall back to the legacy severity-based status. Drives the `limited_by_setup`
   * vs `conflict` distinction.
   */
  outcome: string | null
  /** Stronger hard rules that forced the shortfall (from `context.blocked_by`). */
  blockedBy: BlockedByEntry[]
  /** Resolved team name (from `context.team_uid` or `affected_team_uids`). */
  teamLabel: string | null
  /** Readable scope: e.g. "Week 3" or the division name. */
  scopeLabel: string | null
  /** Observed value from `context` (e.g. game_count), else null. */
  actual: number | null
  /** Target value from `context`, else null. */
  target: number | null
  message: string
  suggestion: string | null
  /**
   * Affected games for the "Game N" chips. `gameNumber` is the per-division
   * number (the jump key, paired with `divisionId`); `displayNumber` is the
   * flight-unique number shown on the chip (falls back to `gameNumber`).
   */
  gameRefs: { gameNumber: number; displayNumber: number }[]
  /**
   * Division this violation resolves to (team-scope-first, via `entryDivisionId`).
   * Disambiguates a duplicate `game_number` when jumping to the offending game —
   * `game_number` is unique only within a division. Null when no team/game scope
   * resolves a single division.
   */
  divisionId: string | null
  isPrevented: boolean
}

export interface ConstraintRuleEntry {
  /** The defining constraint's id — the stable rule identity. */
  id: string
  paramSummary: string | null
  isHard: boolean
  isEnabled: boolean
  scopeLabel: string | null
  status: ConstraintReportStatus
  violations: ViolationEntry[]
}

export interface ConstraintReportRollup {
  conflicts: number
  advisories: number
  info: number
}

export interface ConstraintReportGroup {
  code: string
  displayName: string
  category: string
  status: ConstraintReportStatus
  rollup: ConstraintReportRollup
  rules: ConstraintRuleEntry[]
  /** Violations of this code that matched no single enabled rule. */
  orphanViolations: ViolationEntry[]
}

export interface BuildConstraintReportInput {
  constraints: SchedulerConstraint[]
  violations: SchedulerViolation[]
  /** team_uid → display name. */
  teamNames: Record<string, string>
  /** division_uid → display name. */
  divisionNames: Map<string, string>
  /** team_uid → division_uid (its home division). */
  teamDivisionByTeamUid: Map<string, string>
  /** game_number → division_uid. */
  gameDivisionByNumber: Map<number, string>
  /** `${division_uid}#${game_number}` → flight-unique global_game_number. */
  globalGameNumberByKey: Map<string, number>
}

// ── Status helpers ────────────────────────────────────────────────────────────

// Worst → best. `conflict` (an avoidable hard miss the admin can fix) outranks
// `setup_impossible` (blocking, but a setup truth). `limited_by_setup` is a
// forced shortfall — NOT a penalty — so it ranks just above `ok`.
const STATUS_RANK: Record<ConstraintReportStatus, number> = {
  conflict: 0,
  setup_impossible: 1,
  advisory: 2,
  limited_by_setup: 3,
  ok: 4,
  disabled: 5,
}

const STATUS_BY_SEVERITY: ConstraintReportStatus[] = [
  'conflict',
  'setup_impossible',
  'advisory',
  'limited_by_setup',
  'ok',
]

const worstStatus = (
  statuses: ConstraintReportStatus[]
): ConstraintReportStatus => {
  for (const candidate of STATUS_BY_SEVERITY) {
    if (statuses.includes(candidate)) {
      return candidate
    }
  }
  return 'disabled'
}

/**
 * The effective status of a single entry. When `context.outcome` is present
 * (the constraint-semantics path) it drives the calm `limited_by_setup` /
 * blocking `setup_impossible` states; otherwise the entry falls back to the
 * legacy severity-based classification (conflict/advisory/ok), so historical and
 * deploy-skew rows are unchanged.
 */
const entryStatus = (e: ViolationEntry): ConstraintReportStatus => {
  if (e.outcome === 'forced_shortfall') {
    return 'limited_by_setup'
  }
  if (
    e.outcome === 'setup_impossible' ||
    e.outcome === 'infeasible_configuration'
  ) {
    return 'setup_impossible'
  }
  const cls = classifyViolation(e.severity, e.isPrevented)
  if (cls === 'conflict') {
    return 'conflict'
  }
  if (cls === 'advisory') {
    return 'advisory'
  }
  return 'ok'
}

/**
 * Derive a rule/orphan status from its violation entries + enabled flag. An
 * ENABLED rule with no qualifying entries is `ok` (NOT `disabled`) — unlike
 * {@link worstStatus}, whose empty/all-disabled fallback is `disabled`.
 */
const statusFromEntries = (
  entries: ViolationEntry[],
  isEnabled: boolean
): ConstraintReportStatus => {
  if (!isEnabled) {
    return 'disabled'
  }
  const statuses = entries.map(entryStatus)
  for (const candidate of STATUS_BY_SEVERITY) {
    if (statuses.includes(candidate)) {
      return candidate
    }
  }
  return 'ok'
}

const rollupOf = (entries: ViolationEntry[]): ConstraintReportRollup => {
  const c = countViolations(
    entries.map((e) => ({ severity: e.severity, is_prevented: e.isPrevented }))
  )
  return { conflicts: c.conflicts, advisories: c.advisories, info: c.info }
}

const ENTRY_RANK: Record<ViolationSeverity, number> = {
  error: 0,
  warning: 1,
  info: 2,
}

/** conflicts → advisories → info; prevented sinks within its severity. */
const sortEntries = (entries: ViolationEntry[]): ViolationEntry[] =>
  [...entries].sort((a, b) => {
    if (a.isPrevented !== b.isPrevented) {
      return a.isPrevented ? 1 : -1
    }
    return ENTRY_RANK[a.severity] - ENTRY_RANK[b.severity]
  })

// ── Context / scope helpers ───────────────────────────────────────────────────

const asString = (v: unknown): string | null =>
  typeof v === 'string' && v.length > 0 ? v : null

const asNumber = (v: unknown): number | null => {
  if (typeof v === 'number' && Number.isFinite(v)) {
    return v
  }
  if (typeof v === 'string' && v.trim() !== '' && !Number.isNaN(Number(v))) {
    return Number(v)
  }
  return null
}

/** Resolve a single division for a violation from team/game tags. */
const entryDivisionId = (
  v: SchedulerViolation,
  ctx: Record<string, unknown>,
  teamDivisionByTeamUid: Map<string, string>,
  gameDivisionByNumber: Map<number, string>
): string | null => {
  const ctxTeam = asString(ctx.team_uid)
  if (ctxTeam && teamDivisionByTeamUid.has(ctxTeam)) {
    return teamDivisionByTeamUid.get(ctxTeam) ?? null
  }
  for (const t of v.affected_team_uids ?? []) {
    if (teamDivisionByTeamUid.has(t)) {
      return teamDivisionByTeamUid.get(t) ?? null
    }
  }
  for (const g of v.affected_game_numbers ?? []) {
    if (gameDivisionByNumber.has(g)) {
      return gameDivisionByNumber.get(g) ?? null
    }
  }
  return null
}

/** Readable per-entry scope label, preferring week context then division. */
const entryScopeLabel = (
  v: SchedulerViolation,
  ctx: Record<string, unknown>,
  divisionNames: Map<string, string>,
  teamDivisionByTeamUid: Map<string, string>,
  gameDivisionByNumber: Map<number, string>
): string | null => {
  const weekStr = asString(ctx.week_number)
  if (weekStr) {
    // Solver weeks look like "2027w3" — surface the human week number.
    const m = weekStr.match(/w(\d+)\s*$/i)
    return m ? `Week ${m[1]}` : `Week ${weekStr}`
  }
  const weekNum = asNumber(ctx.week_number)
  if (weekNum != null) {
    return `Week ${weekNum}`
  }
  const divId = entryDivisionId(
    v,
    ctx,
    teamDivisionByTeamUid,
    gameDivisionByNumber
  )
  return divId ? divisionNames.get(divId) ?? null : null
}

const toViolationEntry = (
  v: SchedulerViolation,
  input: BuildConstraintReportInput
): ViolationEntry => {
  const ctx = v.context ?? {}
  const ctxTeam = asString(ctx.team_uid)
  const teamUids = ctxTeam ? [ctxTeam] : v.affected_team_uids ?? []
  const divisionId = entryDivisionId(
    v,
    ctx,
    input.teamDivisionByTeamUid,
    input.gameDivisionByNumber
  )
  // Display the flight-unique number on each chip while keeping the per-division
  // number as the jump key (paired with the resolved division).
  const gameRefs = (v.affected_game_numbers ?? []).map((gameNumber) => {
    const global =
      divisionId != null
        ? input.globalGameNumberByKey.get(`${divisionId}#${gameNumber}`)
        : undefined
    return { gameNumber, displayNumber: global ?? gameNumber }
  })
  return {
    severity: v.severity,
    outcome: asString(ctx.outcome),
    blockedBy: readBlockedBy(ctx),
    teamLabel: resolveTeamLabel(teamUids, input.teamNames),
    scopeLabel: entryScopeLabel(
      v,
      ctx,
      input.divisionNames,
      input.teamDivisionByTeamUid,
      input.gameDivisionByNumber
    ),
    actual: asNumber(ctx.game_count),
    target: asNumber(ctx.target),
    message: humanizeWeekRefs(v.message),
    suggestion: v.resolution_suggestion,
    gameRefs,
    divisionId,
    isPrevented: v.is_prevented,
  }
}

// ── Rule scope label ──────────────────────────────────────────────────────────

const namesOrCount = (
  uids: string[],
  lookup: (uid: string) => string | undefined,
  noun: string
): string => {
  const names = uids.map(lookup).filter((n): n is string => Boolean(n))
  if (names.length > 0 && names.length <= 2 && names.length === uids.length) {
    return names.join(', ')
  }
  return `${uids.length} ${noun}${uids.length === 1 ? '' : 's'}`
}

const ruleScopeLabel = (
  c: SchedulerConstraint,
  divisionNames: Map<string, string>,
  teamNames: Record<string, string>
): string | null => {
  const divs = c.division_uids ?? []
  const teams = c.team_uids ?? []
  if (divs.length === 0 && teams.length === 0) {
    return null // unscoped → applies league-wide
  }
  const parts: string[] = []
  if (divs.length > 0) {
    parts.push(namesOrCount(divs, (d) => divisionNames.get(d), 'division'))
  }
  if (teams.length > 0) {
    parts.push(namesOrCount(teams, (t) => teamNames[t], 'team'))
  }
  return parts.join(' · ')
}

// ── Footprint / matching ──────────────────────────────────────────────────────

interface Footprint {
  teams: Set<string>
  divisions: Set<string>
}

const violationFootprint = (
  v: SchedulerViolation,
  ctx: Record<string, unknown>,
  teamDivisionByTeamUid: Map<string, string>,
  gameDivisionByNumber: Map<number, string>
): Footprint => {
  const teams = new Set<string>()
  const ctxTeam = asString(ctx.team_uid)
  if (ctxTeam) {
    teams.add(ctxTeam)
  }
  for (const t of v.affected_team_uids ?? []) {
    teams.add(t)
  }
  const divisions = new Set<string>()
  for (const t of teams) {
    const d = teamDivisionByTeamUid.get(t)
    if (d) {
      divisions.add(d)
    }
  }
  for (const g of v.affected_game_numbers ?? []) {
    const d = gameDivisionByNumber.get(g)
    if (d) {
      divisions.add(d)
    }
  }
  return { teams, divisions }
}

const isUnscoped = (c: SchedulerConstraint): boolean =>
  (c.team_uids?.length ?? 0) === 0 && (c.division_uids?.length ?? 0) === 0

/**
 * Codes whose `team_uids` is a SPECIFIC matchup — every team in the set is part
 * of one indivisible pairing — rather than a list of independently-scoped teams.
 * For these, a violation must cover the whole pair to attach. Everything else
 * team-scoped (`preferred_time`, `team_bye_period`, `preferred_day`, …) lists
 * teams that are each affected on their own, so any one of them overlapping is
 * enough.
 */
const PAIR_SEMANTIC_CODES = new Set(['pair_matchup_count'])

/**
 * Does a scoped rule match the violation footprint? Pair-semantic team rules
 * (`pair_matchup_count`) require ALL of the rule's teams to be present — an A+B
 * matchup rule must NOT claim a violation involving A+C or B+D, which any-overlap
 * would make ambiguous (orphaning the real pair while the pair rule reads OK).
 * Ordinary team-scoped rules keep any-overlap intersection: a violation for team
 * A alone still attaches to a `preferred_time` rule scoped to [A, B]. Division-
 * scoped rules also use any-overlap intersection.
 */
const ruleMatchesFootprint = (
  c: SchedulerConstraint,
  fp: Footprint
): boolean => {
  const teams = c.team_uids ?? []
  if (teams.length > 0) {
    return PAIR_SEMANTIC_CODES.has(c.type.code)
      ? teams.every((t) => fp.teams.has(t))
      : teams.some((t) => fp.teams.has(t))
  }
  const divs = c.division_uids ?? []
  if (divs.length > 0) {
    return divs.some((d) => fp.divisions.has(d))
  }
  return false // unscoped handled as a catch-all elsewhere
}

// ── Main builder ──────────────────────────────────────────────────────────────

/**
 * Minimal type info a group needs for its heading. Both a defined constraint's
 * `type` and a violation's `constraint_type` satisfy it (the latter widens
 * `category` to `string`), so either can seed the group.
 */
interface ConstraintTypeInfo {
  id: string
  code: string
  name: string
  category: string
}

interface GroupAccumulator {
  code: string
  type: ConstraintTypeInfo | null
  constraints: SchedulerConstraint[]
  /** constraint.id → attached violations. */
  byRule: Map<string, SchedulerViolation[]>
  orphans: SchedulerViolation[]
}

const codeOf = (v: SchedulerViolation): string =>
  v.constraint_type?.code ?? 'unknown'

export function buildConstraintReport(
  input: BuildConstraintReportInput
): ConstraintReportGroup[] {
  const { constraints } = input
  const violations = dedupeViolations(input.violations)

  // Seed one accumulator per code, in first-appearance order. Each defined
  // constraint becomes a rule keyed by its id (duplicate codes preserved).
  const groups = new Map<string, GroupAccumulator>()
  const ensureGroup = (
    code: string,
    type: ConstraintTypeInfo | null
  ): GroupAccumulator => {
    let g = groups.get(code)
    if (!g) {
      g = { code, type, constraints: [], byRule: new Map(), orphans: [] }
      groups.set(code, g)
    } else if (!g.type && type) {
      g.type = type
    }
    return g
  }

  for (const c of constraints) {
    const g = ensureGroup(c.type.code, c.type)
    g.constraints.push(c)
    g.byRule.set(c.id, [])
  }

  // Attach each violation to exactly one enabled rule, else to orphans.
  for (const v of violations) {
    const code = codeOf(v)
    const g = ensureGroup(code, v.constraint_type)
    const enabled = g.constraints.filter((c) => c.is_enabled)

    if (g.constraints.length === 0) {
      g.orphans.push(v) // code has no defined rule at all
      continue
    }
    if (enabled.length === 1) {
      g.byRule.get(enabled[0].id)!.push(v) // single enabled row takes all
      continue
    }
    if (enabled.length === 0) {
      g.orphans.push(v) // emitted against a disabled code — surface it
      continue
    }

    const ctx = v.context ?? {}
    const fp = violationFootprint(
      v,
      ctx,
      input.teamDivisionByTeamUid,
      input.gameDivisionByNumber
    )
    const scoped = enabled.filter((c) => ruleMatchesFootprint(c, fp))
    if (scoped.length === 1) {
      g.byRule.get(scoped[0].id)!.push(v)
    } else if (scoped.length === 0) {
      const unscoped = enabled.filter(isUnscoped)
      if (unscoped.length === 1) {
        g.byRule.get(unscoped[0].id)!.push(v)
      } else {
        g.orphans.push(v)
      }
    } else {
      g.orphans.push(v) // ambiguous — matched more than one rule
    }
  }

  // Materialize groups.
  const result: ConstraintReportGroup[] = []
  for (const g of groups.values()) {
    const rules: ConstraintRuleEntry[] = g.constraints.map((c) => {
      const entries = sortEntries(
        (g.byRule.get(c.id) ?? []).map((v) => toViolationEntry(v, input))
      )
      return {
        id: c.id,
        paramSummary: formatConstraintParameterSummary(
          c.type.code,
          c.parameters
        ),
        isHard: c.is_hard,
        isEnabled: c.is_enabled,
        scopeLabel: ruleScopeLabel(c, input.divisionNames, input.teamNames),
        status: statusFromEntries(entries, c.is_enabled),
        violations: entries,
      }
    })

    const orphanEntries = sortEntries(
      g.orphans.map((v) => toViolationEntry(v, input))
    )

    const allEntries = [...rules.flatMap((r) => r.violations), ...orphanEntries]
    const ruleStatuses = rules.map((r) => r.status)
    const groupStatus = worstStatus([
      ...ruleStatuses,
      ...(orphanEntries.length > 0
        ? [statusFromEntries(orphanEntries, true)]
        : []),
    ])

    // Sort rules within the group by status (conflict → advisory → ok → disabled).
    rules.sort((a, b) => STATUS_RANK[a.status] - STATUS_RANK[b.status])

    result.push({
      code: g.code,
      displayName: g.type
        ? getConstraintDisplayName(g.code, g.type.name)
        : g.code,
      category: g.type ? mapCategoryToDisplay(g.type.category) : 'Other',
      status: groupStatus,
      rollup: rollupOf(allEntries),
      rules,
      orphanViolations: orphanEntries,
    })
  }

  // Sort groups: conflicts → advisories → satisfied → disabled (stable within).
  return result.sort((a, b) => STATUS_RANK[a.status] - STATUS_RANK[b.status])
}
